import { Checkbox, Grid } from '@mui/material';
import PanelHeader from '../components/panelheader/panelHeader';
import * as css from './steder.uha.module.scss';
import DatePicker from '../components/datepicker';
import Button from '../components/button';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { formatIsoTzLess } from '../date.utils';
import translation from './activity.uha.i18n';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import { NumberInput } from '../components/input';
import classNames from 'classnames';
import { useActivityContextUHA } from './activitycontexts';
import dayjs from 'dayjs';
import useAddErrorNotificationCallback from '../hooks/useAddErrorNotificationCallback';
import { useFormatMessageFor } from '../i18n/translation';

const Onskeliste = () => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const errorNotificationCallback = useAddErrorNotificationCallback(() => {
    setMaxOnsker(confirmedMaxOnsker);
  });
  const { praksisaktivitet } = useActivityContextUHA();
  const labels = useFormatMessageFor(translation);

  const {
    id: aktivitetId,
    avtaler,
    onskeliste,
    delt,
    kanEndre,
  } = praksisaktivitet;
  const { aapnet, aktivert, kanEndresFra, kanEndresTil, antallOnsker } =
    onskeliste;

  const [activityChangedLabel, setActivityChangedLabel] = useState('');
  const [onskelisteAapnet, setOnskelisteAapnet] = useState<boolean>(aapnet);
  const [onskelisteTilDato, setOnskelisteTilDato] = useState<
    string | undefined
  >(kanEndresTil);
  const [benytterOnskeliste, setBenytterOnskeliste] =
    useState<boolean>(aktivert);
  const [confirmedMaxOnsker, setConfirmedMaxOnsker] =
    useState<number>(antallOnsker);
  const [maxOnsker, setMaxOnsker] = useState<number | undefined>(
    confirmedMaxOnsker,
  );
  const [maxOnskerHasChanged, setMaxOnskerHasChanged] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const onBenytterOnskelisteChange = (value: boolean) => {
    setBenytterOnskeliste(value);
    setActivityChangedLabel(
      value ? labels.onskelisteAktivert : labels.onskelisteDeaktivert,
    );
  };

  const onOnskelisteFristChange = (newFrist: string) => {
    setOnskelisteTilDato(newFrist);
    setActivityChangedLabel(labels.onskelisteTidspunktEndret);
  };

  const onOnskelisteAapnetChange = (aapnet: boolean) => {
    setOnskelisteAapnet(aapnet);
    setActivityChangedLabel(
      aapnet ? labels.onskelisteAapnet : labels.onskelisteAvsluttet,
    );
  };

  const onConfirmedMaxOnsker = () => {
    if (maxOnsker) {
      setActivityChangedLabel(labels.endretAntallOnsker);
    }
  };

  const save = useCallback(() => {
    setIsSaving(true);

    praksiskalenderApi
      .praksisaktivitetIdOnskelistePut({
        pathParams: { id: aktivitetId },
        praksisaktivitetOnskelisteInfoDto: {
          aapnet: onskelisteAapnet,
          aktivert: benytterOnskeliste,
          kanEndresFra: benytterOnskeliste
            ? kanEndresFra
              ? kanEndresFra
              : formatIsoTzLess(new Date())
            : undefined,
          kanEndresTil: benytterOnskeliste ? onskelisteTilDato : undefined,
          antallOnsker: maxOnsker || 10,
        },
      })
      .then(() => {
        addSuccessNotification(activityChangedLabel);
        setConfirmedMaxOnsker(maxOnsker || 10);
      })
      .catch(errorNotificationCallback)
      .finally(() => {
        setActivityChangedLabel('');
        setIsSaving(false);
      });
  }, [
    activityChangedLabel,
    errorNotificationCallback,
    addSuccessNotification,
    aktivitetId,
    benytterOnskeliste,
    kanEndresFra,
    onskelisteAapnet,
    onskelisteTilDato,
    praksiskalenderApi,
    maxOnsker,
  ]);

  useEffect(() => {
    if (activityChangedLabel !== '' && !isSaving) {
      save();
    }
  }, [activityChangedLabel, isSaving, save]);

  const canOpenOnskeliste = () => {
    if (
      !kanEndre ||
      !delt ||
      !benytterOnskeliste ||
      !avtaler ||
      avtaler.length === 0 ||
      !onskelisteTilDato ||
      (onskelisteTilDato &&
        (onskelisteTilDato < formatIsoTzLess(new Date()) ||
          onskelisteTilDato >= praksisaktivitet.startDato))
    ) {
      return false;
    }

    return !!avtaler.find(
      (a) => a.praksissted.kapasitet && a.praksissted.kapasitet > 0,
    );
  };

  useEffect(() => {
    const changed = maxOnsker !== confirmedMaxOnsker;
    if (changed !== maxOnskerHasChanged) {
      setMaxOnskerHasChanged(changed);
    }
  }, [
    maxOnsker,
    confirmedMaxOnsker,
    maxOnskerHasChanged,
    setMaxOnskerHasChanged,
  ]);

  const lesBeskrivelse = onskelisteAapnet
    ? labels.onskelisteAapenLES
    : labels.onskelisteLukketLES;
  const tilFraLabel = onskelisteAapnet
    ? labels.onskelisteEndreDato
    : labels.onskelisteTilDato;

  return (
    <Grid item={true} className="onskeliste">
      <PanelHeader title={labels.onskeliste} />
      <Grid container={true} spacing={3}>
        <Grid item={true} xs={12} className={css.onskelistebeskrivelse}>
          <p>{kanEndre ? labels.onskelistebeskrivelse : lesBeskrivelse}</p>
        </Grid>
        <Grid item={true} xs={8} className={css.onskelisteTilDatoLabel}>
          <p>{kanEndre ? tilFraLabel : labels.onskelisteSvarfristLES}</p>
          <DatePicker
            value={onskelisteTilDato || ''}
            disabled={!benytterOnskeliste || !kanEndre}
            onChange={(newFrist) => {
              if (onskelisteTilDato !== newFrist) {
                onOnskelisteFristChange(newFrist);
              }
            }}
            constraints={{
              maxDate: dayjs(praksisaktivitet.startDato),
            }}
            dataCy="onskelisteDatePicker"
          />
        </Grid>
        <Grid item={true} xs={4} className={css.onskelisteButtons}>
          {kanEndre &&
            (onskelisteAapnet ? (
              <Button.Secondary
                disabled={!onskelisteAapnet}
                onClick={() => onOnskelisteAapnetChange(false)}
                className="avsluttOnskelisteButton"
              >
                {labels.closeOnskeliste}
              </Button.Secondary>
            ) : (
              <Button.Secondary
                disabled={!canOpenOnskeliste()}
                onClick={() => onOnskelisteAapnetChange(true)}
                className="aapneOnskelisteButton"
              >
                {labels.openOnskeliste}
              </Button.Secondary>
            ))}
        </Grid>
        <Grid item={true} xs={8} className={css.onskelisteTilDatoLabel}>
          <p>
            {kanEndre
              ? labels.antallOnskerLabel
              : labels.onskelisteMaksgrenseLES}
          </p>
          <span>
            <NumberInput
              onChange={(value) => {
                setMaxOnsker(value);
              }}
              containerClassName={css.maxOnsker}
              htmlId={'num-wishes'}
              ariaLabel={labels.antallOnsker}
              value={maxOnsker}
              min={1}
              max={99}
              disabled={!kanEndre}
            />
          </span>
          <FormGroup row>
            <FormControlLabel
              className={classNames(css.onskelisteCheckbox, css.checboxPadding)}
              control={
                <Checkbox
                  color="secondary"
                  size="medium"
                  checked={!benytterOnskeliste}
                  disabled={onskelisteAapnet || !kanEndre}
                  onChange={(e) => {
                    onBenytterOnskelisteChange(!e.target.checked);
                  }}
                />
              }
              label={labels.dontOfferOnskeliste}
            />
          </FormGroup>
        </Grid>
        <Grid item={true} xs={4} className={css.onskelisteButtons}>
          {kanEndre && (
            <Button.Secondary
              disabled={!maxOnskerHasChanged || !maxOnsker}
              onClick={onConfirmedMaxOnsker}
            >
              {labels.bekreftAntallforandring}
            </Button.Secondary>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Onskeliste;
